import React, { useState, useEffect } from "react";
import {
  Play,
  Plus,
  Trash2,
  DollarSign,
  RotateCw,
  Download,
  AlertTriangle,
  Beaker,
  Rocket,
  Check,
  ChevronDown,
  ChevronUp,
  MessageSquare,
} from "lucide-react";
import { ExperimentationService } from "../../services/experimentation.service";
import type { ExperimentResult } from "../../services/experimentation.service";
import { Modal } from "../common/Modal";
import { ResultSummaryPanel } from "./ResultSummaryPanel";
import { useDebounce } from "../../hooks/useDebounce";
import { AWS_BEDROCK_PRICING } from "../../utils/pricing";

interface ModelConfig {
  provider: string;
  model: string;
  temperature: number;
  maxTokens: number;
}

interface AvailableModel {
  provider: string;
  model: string;
  modelName?: string;
  modelId?: string;
  status?: "available" | "unavailable";
  availabilityStatus?: "available" | "not_configured" | "requires_bedrock";
  pricing: {
    input: number;
    output: number;
    unit: string;
  };
  capabilities: string[];
  contextWindow: number;
  category?: string;
  isLatest?: boolean;
  notes?: string;
}

/** AI evaluation reasoning - can be string or per-criterion object from AI judge */
type AIReasoning =
  | string
  | Record<
    string,
    string | { strengths?: string[]; weaknesses?: string[] } | undefined
  >;

interface ComparisonResult {
  id?: string;
  model: string;
  provider: string;
  response?: string;
  bedrockOutput?: string;
  metrics?: {
    cost: number;
    latency: number;
    tokenCount: number;
    qualityScore: number;
    errorRate: number;
  };
  performance?: {
    responseTime: number;
    throughput: number;
    reliability: number;
  };
  costBreakdown?: {
    inputTokens: number;
    outputTokens: number;
    inputCost: number;
    outputCost: number;
    totalCost: number;
  };
  actualCost?: number;
  executionTime?: number;
  aiEvaluation?: {
    model?: string;
    overall_score?: number;
    overallScore?: number;
    criteria_scores?: Record<string, number>;
    criterion_scores?: Record<string, number>;
    criteriaScores?: Record<string, number>;
    reasoning?: AIReasoning;
    recommendation?: string;
    strengths?: string[];
    weaknesses?: string[];
  };
  actualUsage?: {
    totalCalls: number;
    avgCost: number;
    avgTokens: number;
    avgResponseTime: number;
    errorRate: number;
    totalCost: number;
  };
  noUsageData?: boolean;
  estimatedCostPer1K?: number;
  recommendation?: string;
  analysis?: {
    strengths: string[];
    considerations: string[];
  };
  pricing?: {
    inputCost: number;
    outputCost: number;
    contextWindow: number;
  };
}

interface ComparisonAnalysis {
  winner?: { model: string; reason: string };
  costPerformanceAnalysis?: string;
  useCaseRecommendations?: string[];
}

const ModelComparison: React.FC = () => {
  const [prompt, setPrompt] = useState("");
  const [selectedModels, setSelectedModels] = useState<ModelConfig[]>([]);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState<string[]>([
    "accuracy",
    "relevance",
    "completeness",
  ]);
  const [iterations, setIterations] = useState(1);
  const [results, setResults] = useState<ComparisonResult[]>([]);
  const [currentExperiment, setCurrentExperiment] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState<ComparisonResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [realTimeMode, setRealTimeMode] = useState(false);
  const [progressData, setProgressData] = useState<any>(null);
  const [comparisonAnalysis, setComparisonAnalysis] =
    useState<ComparisonAnalysis | null>(null);
  const [comparisonMode, setComparisonMode] = useState<
    "quality" | "cost" | "speed" | "comprehensive"
  >("comprehensive");
  const [executeOnBedrock, setExecuteOnBedrock] = useState(true);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [allModels, setAllModels] = useState<AvailableModel[]>([]);
  const [expandedResponseIndex, setExpandedResponseIndex] = useState<number | null>(null);
  const [promptSetMode, setPromptSetMode] = useState(false);
  const [promptsText, setPromptsText] = useState("");
  const [comparisonHistory, setComparisonHistory] = useState<ExperimentResult[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [fineTuningOpen, setFineTuningOpen] = useState(false);
  const [fineTuningLoading, setFineTuningLoading] = useState(false);
  const [fineTuningData, setFineTuningData] = useState<unknown>(null);

  // Add debouncing for prompt to prevent API calls on every keystroke
  const debouncedPrompt = useDebounce(prompt, 800); // Wait 800ms after user stops typing

  const criteriaOptions = [
    "accuracy",
    "relevance",
    "completeness",
    "coherence",
    "creativity",
    "factual_accuracy",
    "safety",
    "bias_detection",
    "language_quality",
  ];

  useEffect(() => {
    loadAvailableModels();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    ExperimentationService.getModelComparisonHistory({ limit: 15 })
      .then((data) => {
        if (!cancelled) setComparisonHistory(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setComparisonHistory([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only trigger cost estimation with debounced prompt to avoid API spam
  useEffect(() => {
    if (selectedModels.length > 0 && debouncedPrompt.trim()) {
      console.log("Triggering cost estimation with debounced prompt...");
      estimateComparisonCost();
    } else if (!debouncedPrompt.trim()) {
      setEstimatedCost(0);
    }
  }, [selectedModels, debouncedPrompt, iterations]);

  useEffect(() => {
    if (selectedModels.length > 0 && prompt.trim()) {
      estimateComparisonCost();
    }
  }, [selectedModels, prompt, iterations]);

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await ExperimentationService.getAvailableModels();

      const rawModels = Array.isArray(response) ? response : [];
      const normalize = (model: Record<string, unknown>) => ({
        provider: (model.provider as string) || "Unknown",
        model: String(model.model || model.modelId || model.modelName || ""),
        modelName: String(model.modelName || model.model || ""),
        status: (model.status as "available" | "unavailable") ?? "available",
        pricing: {
          input: Number((model.pricing as { input?: number })?.input) || 0,
          output: Number((model.pricing as { output?: number })?.output) || 0,
          unit: String((model.pricing as { unit?: string })?.unit) || "Per 1M tokens",
        },
        capabilities: (model.capabilities as string[]) || ["text"],
        contextWindow: Number(model.contextWindow) || 8192,
        category: (model.category as string) || "general",
        isLatest: model.isLatest !== false,
        notes: (model.notes as string) || "",
        availabilityStatus:
          (model.availabilityStatus as AvailableModel["availabilityStatus"]) ??
          ((model.status as string) === "available"
            ? "available"
            : "not_configured"),
      });

      const normalizedAll = rawModels.map(normalize);
      const availableOnly = normalizedAll.filter(
        (m) => !m.status || m.status === "available",
      );

      setAllModels(normalizedAll);
      setAvailableModels(availableOnly);
      setError(null);

      if (availableOnly.length === 0 && normalizedAll.length > 0) {
        setError(
          "No models available. All models from the backend are currently unavailable.",
        );
      } else if (availableOnly.length === 0) {
        setError(
          "No models available from the backend. Please check your AWS Bedrock configuration.",
        );
      }

      if (selectedModels.length > 0) {
        setTimeout(estimateComparisonCost, 200);
      }
    } catch (error: unknown) {
      console.error("Error loading available models:", error);
      setError(
        "Failed to load available models: " +
        (error instanceof Error ? error.message : "Unknown error"),
      );
      setAvailableModels([]);
      setAllModels([]);
      // Fallback: use AWS Bedrock pricing for common models
      try {
        const { AWS_BEDROCK_PRICING } = await import("../../utils/pricing");
        const fallback = AWS_BEDROCK_PRICING.filter(
          (m) =>
            (m.category === "text" || m.category === "multimodal") &&
            m.inputPrice > 0 &&
            m.outputPrice > 0,
        )
          .sort((a, b) => a.inputPrice + a.outputPrice - (b.inputPrice + b.outputPrice))
          .slice(0, 15)
          .map((m) => ({
            provider: m.provider,
            model: m.model,
            modelName: m.model,
            status: "available" as const,
            pricing: {
              input: m.inputPrice * 1000,
              output: m.outputPrice * 1000,
              unit: "Per 1M tokens",
            },
            capabilities: m.capabilities || ["text"],
            contextWindow: m.contextWindow || 8192,
            category: m.category || "general",
            isLatest: m.isLatest !== false,
            notes: "",
          }));
        setAvailableModels(fallback);
        setAllModels(fallback);
        setError("Using fallback model list. Check your connection.");
      } catch {
        // Keep empty
      }
    } finally {
      setModelsLoading(false);
    }
  };

  const estimateComparisonCost = async () => {
    try {
      // Calculate cost estimate based on prompt length and selected models
      if (!debouncedPrompt.trim() || selectedModels.length === 0) {
        setEstimatedCost(0);
        return;
      }

      const promptTokens = Math.ceil(debouncedPrompt.length / 4); // Rough token estimate
      const outputTokens = 500; // Estimated response length
      let totalCost = 0;

      selectedModels.forEach((selectedModel) => {
        const modelPricing = getModelPricing(
          selectedModel.provider,
          selectedModel.model,
        );
        if (modelPricing) {
          const inputCost = (promptTokens / 1000000) * modelPricing.input;
          const outputCost = (outputTokens / 1000000) * modelPricing.output;
          totalCost += (inputCost + outputCost) * iterations;
        }
      });

      setEstimatedCost(totalCost);

      // Also try backend estimation as fallback
      try {
        const estimate = await ExperimentationService.estimateExperimentCost({
          type: "model_comparison",
          parameters: {
            prompt: debouncedPrompt,
            models: selectedModels,
            iterations,
          },
        });

        if (estimate.estimatedCost > 0) {
          setEstimatedCost(estimate.estimatedCost);
        }
      } catch {
        console.log(
          "Backend cost estimation unavailable, using frontend calculation:",
          totalCost,
        );
      }
    } catch (error) {
      console.error("Error estimating cost:", error);
      setEstimatedCost(0);
    }
  };

  const addModel = () => {
    console.log(
      "addModel called. Available models:",
      availableModels.length,
      "Selected models:",
      selectedModels.length,
    );

    if (availableModels.length === 0) {
      setError("No models available to add. Please wait for models to load.");
      return;
    }

    // Find a model that hasn't been selected yet
    const usedModels = new Set(
      selectedModels.map((m) => `${m.provider}:${m.model}`),
    );
    console.log("Used models:", Array.from(usedModels));

    const availableModel = availableModels.find(
      (m) => !usedModels.has(`${m.provider}:${m.model}`),
    );
    console.log("Found available model:", availableModel);

    if (availableModel) {
      const newModel = {
        provider: availableModel.provider,
        model: availableModel.model,
        temperature: 0.7,
        maxTokens: 1000,
      };
      console.log("Adding new model:", newModel);

      setSelectedModels([...selectedModels, newModel]);
      setError(null); // Clear any previous errors

      // Trigger cost recalculation with new model
      setTimeout(estimateComparisonCost, 100);
    } else {
      const message =
        availableModels.length === selectedModels.length
          ? "All available models have been selected."
          : "No more models available to add.";
      setError(message);
    }
  };

  const removeModel = (index: number) => {
    setSelectedModels(selectedModels.filter((_, i) => i !== index));
  };

  const updateModel = (index: number, field: keyof ModelConfig, value: any) => {
    console.log("updateModel called:", { index, field, value });
    const updated = [...selectedModels];

    if (field === "provider" || field === "model") {
      // When changing provider or model, find the corresponding model data
      if (field === "provider") {
        updated[index] = { ...updated[index], [field]: value };
        // Find first available model for this provider
        const availableForProvider = availableModels.find(
          (m) => m.provider === value,
        );
        if (availableForProvider) {
          updated[index].model = availableForProvider.model;
        }
      } else if (field === "model") {
        updated[index] = { ...updated[index], [field]: value };
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    console.log("Updated model:", updated[index]);
    setSelectedModels(updated);
  };

  const runComparison = async () => {
    const multiPrompts = promptSetMode
      ? promptsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
      : [];
    if (promptSetMode && multiPrompts.length === 0) {
      setError("Prompt set mode: enter at least one non-empty line.");
      return;
    }
    if (!promptSetMode && !prompt.trim()) {
      setError("Please provide a prompt and select at least one model");
      return;
    }
    if (selectedModels.length === 0) {
      setError("Select at least one model");
      return;
    }

    // Check for duplicate models
    const modelKeys = selectedModels.map((m) => `${m.provider}:${m.model}`);
    const uniqueModelKeys = new Set(modelKeys);
    if (modelKeys.length !== uniqueModelKeys.size) {
      setError(
        "You have selected duplicate models. Please remove duplicates before running the comparison.",
      );
      return;
    }

    setIsRunning(true);
    setError(null);
    setResults([]);
    setProgressData(null);
    setComparisonAnalysis(null);
    setExpandedResponseIndex(null);

    try {
      if (realTimeMode) {
        await runRealTimeComparison();
      } else {
        await runStaticComparison();
      }
    } catch (error: any) {
      console.error("Error running comparison:", error);
      setIsRunning(false);
      setError(
        "Failed to run comparison: " + (error.message || "Unknown error"),
      );
    }
  };

  const runStaticComparison = async () => {
    const multiPrompts = promptsText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const request = {
      ...(promptSetMode
        ? { prompts: multiPrompts }
        : { prompt: prompt.trim() }),
      models: selectedModels,
      evaluationCriteria,
      iterations,
    };

    console.log("Sending comparison request:", request);

    const experiment = await ExperimentationService.runModelComparison(request);
    console.log("Received experiment result:", experiment);

    setCurrentExperiment(experiment);

    const raw = experiment.results as {
      modelComparisons?: ComparisonResult[];
      results?: ComparisonResult[];
    } | null;
    const list = raw?.modelComparisons ?? raw?.results;
    if (list && list.length > 0) {
      setResults(list);
      console.log("Model comparison results:", list);
    } else {
      setError("No comparison results returned from the experiment");
    }

    setIsRunning(false);
  };

  const runRealTimeComparison = async () => {
    const multiPrompts = promptsText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const request = {
      ...(promptSetMode
        ? { prompts: multiPrompts }
        : { prompt: prompt.trim() }),
      models: selectedModels,
      evaluationCriteria,
      iterations,
      executeOnBedrock,
      comparisonMode,
    };

    try {
      // Start the real-time comparison
      const response =
        await ExperimentationService.startRealTimeComparison(request);
      const { sessionId } = response.data;

      // Connect to SSE for progress updates
      connectToProgressStream(sessionId);
    } catch (error: any) {
      console.error("Error starting real-time comparison:", error);
      setError(
        "Failed to start real-time comparison: " +
        (error.message || "Unknown error"),
      );
      setIsRunning(false);
    }
  };

  const connectToProgressStream = (sessionId: string) => {
    const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
    const eventSource = new EventSource(
      `${API_URL}/api/experimentation/comparison-progress/${sessionId}`,
    );

    let hasCompleted = false;

    const applyProgressPayload = (data: Record<string, unknown>) => {
      setProgressData(data);
      if (data.experimentId && typeof data.experimentId === "string") {
        setCurrentExperiment({ id: data.experimentId });
      }
      if (data.stage === "completed" && data.results) {
        hasCompleted = true;
        setResults(data.results as ComparisonResult[]);
        setComparisonAnalysis(
          (data.analysis as ComparisonAnalysis | null) ?? null,
        );
        setIsRunning(false);
      } else if (data.stage === "failed") {
        setError((data.error as string) || "Comparison failed");
        setIsRunning(false);
      }
    };

    const pollJobRecovery = async () => {
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const job = await ExperimentationService.getComparisonJobState(
            sessionId,
          );
          if (!job) continue;
          setProgressData(job);
          if (job.experimentId) {
            setCurrentExperiment({ id: job.experimentId });
          }
          if (job.stage === "completed" && job.partialResults) {
            hasCompleted = true;
            setResults(job.partialResults as ComparisonResult[]);
            setIsRunning(false);
            return;
          }
          if (job.stage === "failed") {
            setError(job.error || "Comparison failed");
            setIsRunning(false);
            return;
          }
        } catch {
          /* continue polling */
        }
      }
      if (!hasCompleted) {
        setError(
          "Could not recover comparison status. Try refreshing or run again.",
        );
        setIsRunning(false);
      }
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as Record<string, unknown> & {
          type?: string;
        };
        console.log("SSE Progress:", data);

        switch (data.type) {
          case "connection":
            setProgressData({
              stage: "connected",
              progress: 0,
              message: "Connected to comparison stream",
            });
            break;

          case "progress":
            applyProgressPayload(data);
            break;

          case "close":
            eventSource.close();
            if (!hasCompleted) setIsRunning(false);
            break;

          case "heartbeat":
            break;

          default:
            if (data.stage != null) {
              applyProgressPayload(data);
            }
            break;
        }
      } catch (error) {
        console.error("Error parsing SSE data:", error);
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
      if (hasCompleted) return;
      void pollJobRecovery();
    };
  };

  const exportResults = async () => {
    if (!results.length) return;

    try {
      const expId =
        currentExperiment?.id ??
        (currentExperiment as { experimentId?: string })?.experimentId;
      if (expId) {
        const blob = await ExperimentationService.exportExperimentResults(
          expId,
          "json",
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `model-comparison-${expId}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        return;
      }

      const dataToExport = {
        experiment: currentExperiment,
        results: results,
        timestamp: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `model-comparison-local.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting results:", error);
      setError("Failed to export results");
    }
  };

  const getModelPricing = (provider: string, model: string) => {
    const modelData = availableModels.find(
      (m) => m.provider === provider && m.model === model,
    );
    if (
      modelData?.pricing &&
      (modelData.pricing.input > 0 || modelData.pricing.output > 0)
    ) {
      return modelData.pricing;
    }

    // Normalize model ID: strip AWS region prefix (us., eu., ap-south-1., etc.)
    const withoutRegion = model.replace(
      /^(us|eu|ap|sa|ca|me|af)(-[a-z0-9-]+)?\./i,
      "",
    );

    // AWS Bedrock pricing (inputPrice/outputPrice are $/1K; convert to $/1M)
    const bedrockMatch = AWS_BEDROCK_PRICING.find(
      (m) =>
        m.model === model ||
        m.model === withoutRegion ||
        model.includes(m.model) ||
        withoutRegion.includes(m.model),
    );
    if (
      bedrockMatch &&
      bedrockMatch.inputPrice > 0 &&
      bedrockMatch.outputPrice > 0
    ) {
      return {
        input: bedrockMatch.inputPrice * 1000,
        output: bedrockMatch.outputPrice * 1000,
        unit: "Per 1M tokens",
      };
    }

    // Fallback pricing data for common models
    const fallbackPricing: Record<
      string,
      { input: number; output: number; unit: string }
    > = {
      // === OpenAI GPT-5 Models (Latest) ===
      "gpt-5": {
        input: 1.25,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "gpt-5-mini": {
        input: 0.25,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "gpt-5-nano": {
        input: 0.05,
        output: 0.4,
        unit: "Per 1M tokens",
      },
      "gpt-5-chat-latest": {
        input: 1.25,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "gpt-5-chat": {
        input: 1.25,
        output: 10.0,
        unit: "Per 1M tokens",
      },

      // === AWS Models ===
      "amazon.nova-micro-v1:0": {
        input: 0.035,
        output: 0.14,
        unit: "Per 1M tokens",
      },
      "amazon.nova-lite-v1:0": {
        input: 0.06,
        output: 0.24,
        unit: "Per 1M tokens",
      },
      "amazon.nova-pro-v1:0": {
        input: 0.8,
        output: 3.2,
        unit: "Per 1M tokens",
      },
      "amazon.titan-text-lite-v1": {
        input: 0.3,
        output: 0.4,
        unit: "Per 1M tokens",
      },
      "amazon.titan-text-express-v1": {
        input: 0.8,
        output: 1.6,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-sonnet-4-20250514-v1:0": {
        input: 3.0,
        output: 15.0,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-sonnet-4-5-20250929-v1:0": {
        input: 3.0,
        output: 15.0,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-3-5-haiku-20241022-v1:0": {
        input: 0.8,
        output: 4.0,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-opus-4-6-v1": {
        input: 5.0,
        output: 25.0,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-sonnet-4-6": {
        input: 3.0,
        output: 15.0,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-opus-4-1-20250805-v1:0": {
        input: 15.0,
        output: 75.0,
        unit: "Per 1M tokens",
      },
      "meta.llama3-70b-instruct-v1:0": {
        input: 0.59,
        output: 0.79,
        unit: "Per 1M tokens",
      },
      "meta.llama3-8b-instruct-v1:0": {
        input: 0.05,
        output: 0.10,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-3-haiku-20240307-v1:0": {
        input: 0.25,
        output: 1.25,
        unit: "Per 1M tokens",
      },
      "anthropic.claude-3-sonnet-20240229-v1:0": {
        input: 3.0,
        output: 15.0,
        unit: "Per 1M tokens",
      },
      "meta.llama4-scout-17b-instruct-v1:0": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "meta.llama3-2-3b-instruct-v1:0": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "meta.llama3-1-8b-instruct-v1:0": {
        input: 0.22,
        output: 0.22,
        unit: "Per 1M tokens",
      },
      "meta.llama3-1-70b-instruct-v1:0": {
        input: 0.99,
        output: 0.99,
        unit: "Per 1M tokens",
      },
      "command-a-03-2025": {
        input: 2.5,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "command-r7b-12-2024": {
        input: 0.0375,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "command-a-reasoning-08-2025": {
        input: 2.5,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "command-a-vision-07-2025": {
        input: 2.5,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "command-r-plus-04-2024": {
        input: 2.5,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "command-r-08-2024": {
        input: 0.15,
        output: 0.6,
        unit: "Per 1M tokens",
      },
      // === Google AI Models ===
      "gemini-2.5-pro": {
        input: 1.25,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash": {
        input: 0.3,
        output: 2.5,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash-lite-preview": {
        input: 0.1,
        output: 0.4,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash-audio": {
        input: 1.0,
        output: 2.5,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash-lite-audio-preview": {
        input: 0.5,
        output: 0.4,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash-native-audio": {
        input: 0.5,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash-native-audio-output": {
        input: 3.0,
        output: 12.0,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-flash-preview-tts": {
        input: 0.5,
        output: 10.0,
        unit: "Per 1M tokens",
      },
      "gemini-2.5-pro-preview-tts": {
        input: 1.0,
        output: 20.0,
        unit: "Per 1M tokens",
      },
      "gemini-2.0-flash": {
        input: 0.1,
        output: 0.4,
        unit: "Per 1M tokens",
      },
      "gemini-2.0-flash-lite": {
        input: 0.075,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "gemini-2.0-flash-audio": {
        input: 0.7,
        output: 0.4,
        unit: "Per 1M tokens",
      },
      "gemini-1.5-pro": {
        input: 1.25,
        output: 5.0,
        unit: "Per 1M tokens",
      },
      "gemini-1.5-flash": {
        input: 0.075,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "gemini-1.0-pro": {
        input: 1.0,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "gemini-1.0-pro-vision": {
        input: 1.0,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      // === Mistral AI Models ===
      // Premier Models
      "mistral-medium-2508": {
        input: 0.4,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "mistral-medium-latest": {
        input: 0.4,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "magistral-medium-2507": {
        input: 2.0,
        output: 5.0,
        unit: "Per 1M tokens",
      },
      "magistral-medium-latest": {
        input: 2.0,
        output: 5.0,
        unit: "Per 1M tokens",
      },
      "codestral-2508": {
        input: 0.3,
        output: 0.9,
        unit: "Per 1M tokens",
      },
      "codestral-latest": {
        input: 0.3,
        output: 0.9,
        unit: "Per 1M tokens",
      },
      "voxtral-mini-2507": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "voxtral-mini-latest": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "devstral-medium-2507": {
        input: 0.4,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "devstral-medium-latest": {
        input: 0.4,
        output: 2.0,
        unit: "Per 1M tokens",
      },
      "mistral-ocr-2505": {
        input: 1.0,
        output: 3.0,
        unit: "Per 1M tokens",
      },
      "mistral-ocr-latest": {
        input: 1.0,
        output: 3.0,
        unit: "Per 1M tokens",
      },
      "mistral-large-2411": {
        input: 2.0,
        output: 6.0,
        unit: "Per 1M tokens",
      },
      "mistral-large-latest": {
        input: 2.0,
        output: 6.0,
        unit: "Per 1M tokens",
      },
      "pixtral-large-2411": {
        input: 2.0,
        output: 6.0,
        unit: "Per 1M tokens",
      },
      "pixtral-large-latest": {
        input: 2.0,
        output: 6.0,
        unit: "Per 1M tokens",
      },
      "mistral-small-2407": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "mistral-embed": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "codestral-embed-2505": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "mistral-moderation-2411": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "mistral-moderation-latest": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },

      // Open Models
      "magistral-small-2507": {
        input: 0.5,
        output: 1.5,
        unit: "Per 1M tokens",
      },
      "magistral-small-latest": {
        input: 0.5,
        output: 1.5,
        unit: "Per 1M tokens",
      },
      "voxtral-small-2507": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "voxtral-small-latest": {
        input: 0.1,
        output: 0.1,
        unit: "Per 1M tokens",
      },
      "mistral-small-2506": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "devstral-small-2507": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "devstral-small-latest": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "mistral-small-2503": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "mistral-small-2501": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "devstral-small-2505": {
        input: 0.1,
        output: 0.3,
        unit: "Per 1M tokens",
      },
      "pixtral-12b-2409": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "pixtral-12b": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "open-mistral-nemo-2407": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "open-mistral-nemo": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "mistral-nemo": {
        input: 0.15,
        output: 0.15,
        unit: "Per 1M tokens",
      },
      "open-mistral-7b": {
        input: 0.25,
        output: 0.25,
        unit: "Per 1M tokens",
      },
      "open-mixtral-8x7b": {
        input: 0.7,
        output: 0.7,
        unit: "Per 1M tokens",
      },
      "open-mixtral-8x22b": {
        input: 2.0,
        output: 6.0,
        unit: "Per 1M tokens",
      },
      // === Grok AI Models ===
      "grok-4-0709": {
        input: 3.0,
        output: 15.0,
        unit: "Per 1M tokens",
      },
      "grok-3": {
        input: 3.0,
        output: 15.0,
        unit: "Per 1M tokens",
      },
      "grok-3-mini": {
        input: 0.3,
        output: 0.5,
        unit: "Per 1M tokens",
      },
      "grok-2-image-1212": {
        input: 0.07,
        output: 0.07,
        unit: "Per image",
      },
      // === Meta Llama 4 Models ===
      "llama-4-scout": {
        input: 0.19,
        output: 0.49,
        unit: "Per 1M tokens",
      },
      "llama-4-maverick": {
        input: 0.19,
        output: 0.49,
        unit: "Per 1M tokens",
      },
      "llama-4-behemoth-preview": {
        input: 0.19,
        output: 0.49,
        unit: "Per 1M tokens",
      },
    };

    const modelKey =
      `${provider.toLowerCase()}:${model}` in fallbackPricing
        ? `${provider.toLowerCase()}:${model}`
        : model;

    return fallbackPricing[modelKey] || fallbackPricing[model] || null;
  };

  return (
    <div className="glass p-4 sm:p-6 md:p-8 shadow-2xl backdrop-blur-xl animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 md:mb-8 gap-4 sm:gap-0">
        <div className="flex items-center">
          <div className="bg-gradient-primary p-2 sm:p-3 rounded-xl glow-primary shadow-lg mr-3 sm:mr-4">
            <Beaker className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold gradient-text">Model Comparison</h2>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
          {results.length > 0 && (
            <button
              onClick={exportResults}
              className="btn btn-secondary flex items-center justify-center gap-2 text-xs sm:text-sm"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export Results</span>
              <span className="sm:hidden">Export</span>
            </button>
          )}
          <button
            onClick={runComparison}
            disabled={
              isRunning ||
              selectedModels.length === 0 ||
              (!promptSetMode && !prompt.trim()) ||
              (promptSetMode &&
                !promptsText.split("\n").some((s) => s.trim().length > 0))
            }
            className="btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
          >
            {isRunning ? (
              <>
                <RotateCw className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">{realTimeMode ? "Executing Models..." : "Running..."}</span>
                <span className="sm:hidden">Running...</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span className="hidden sm:inline">{realTimeMode ? "Run Real-time Comparison" : "Run Comparison"}</span>
                <span className="sm:hidden">Run</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="glass p-6 mb-6 shadow-2xl backdrop-blur-xl border border-danger-200/30 dark:border-danger-500/30 animate-scale-in">
          <div className="flex items-center">
            <div className="bg-gradient-danger p-2 rounded-lg glow-danger shadow-lg mr-3">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-body text-danger-700 dark:text-danger-300">{error}</span>
          </div>
        </div>
      )}

      {/* Configuration Section */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 mb-4 sm:mb-6 md:mb-8 lg:grid-cols-2">
        {/* Prompt Input */}
        <div className="lg:col-span-2 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm font-display font-semibold text-light-text-primary dark:text-dark-text-primary">
            <input
              type="checkbox"
              checked={promptSetMode}
              onChange={(e) => setPromptSetMode(e.target.checked)}
              className="w-4 h-4 rounded border-primary-300 text-primary-600"
            />
            Prompt set mode (one prompt per line — results aggregated per model)
          </label>
          <label className="label mb-2 sm:mb-3 text-xs sm:text-sm">
            {promptSetMode ? "Prompts (one per line)" : "Test Prompt"}
          </label>
          {promptSetMode ? (
            <textarea
              value={promptsText}
              onChange={(e) => setPromptsText(e.target.value)}
              placeholder="One prompt per line..."
              className="input min-h-[120px] sm:min-h-[160px] resize-y text-sm font-mono"
              rows={6}
            />
          ) : (
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Enter the prompt you want to test across different models..."
              className="input min-h-[100px] sm:min-h-[120px] resize-y text-sm"
              rows={3}
            />
          )}
        </div>

        {/* Real-time Comparison Settings */}
        <div className="lg:col-span-2">
          <div className="glass p-4 sm:p-5 md:p-6 shadow-lg backdrop-blur-xl border border-primary-200/30 space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <label className="flex items-start sm:items-center gap-2 sm:gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={realTimeMode}
                  onChange={(e) => setRealTimeMode(e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 rounded border-primary-300 focus:ring-primary-500 mt-0.5 sm:mt-0 flex-shrink-0"
                />
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm font-display font-semibold text-light-text-primary dark:text-dark-text-primary flex items-center gap-1.5">
                    <Rocket className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400" />
                    Real-time Bedrock Execution
                  </span>
                  <span className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">
                    (Actually runs models for authentic comparison)
                  </span>
                </div>
              </label>
            </div>

            {realTimeMode && (
              <div className="space-y-3 sm:space-y-4 pl-4 sm:pl-6 border-l-4 border-primary-500/30 glass p-3 sm:p-4 rounded-xl">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
                  <label className="flex items-center gap-2 sm:gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={executeOnBedrock}
                      onChange={(e) => setExecuteOnBedrock(e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded border-primary-300 focus:ring-primary-500 flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm font-display font-medium text-light-text-primary dark:text-dark-text-primary">Execute on AWS Bedrock</span>
                  </label>

                  <select
                    value={comparisonMode}
                    onChange={(e) => setComparisonMode(e.target.value as any)}
                    className="input text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <option value="comprehensive">
                      Comprehensive Analysis
                    </option>
                    <option value="quality">Quality Focus</option>
                    <option value="cost">Cost Focus</option>
                    <option value="speed">Speed Focus</option>
                  </select>
                </div>

                <div className="glass p-3 rounded-xl border border-accent-200/30 bg-accent-500/5">
                  <div className="text-xs font-body text-accent-700 dark:text-accent-300 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-accent-600 dark:text-accent-400 flex-shrink-0 mt-0.5" />
                    <span>Real-time mode will take longer but provides authentic
                      model responses and AI-driven evaluation</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress indicator for real-time mode */}
          {isRunning && realTimeMode && progressData && (
            <div className="glass p-6 mt-6 shadow-lg backdrop-blur-xl border border-primary-200/30 animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <div className="spinner"></div>
                <span className="text-sm font-display font-semibold text-primary-700 dark:text-primary-300">
                  {progressData.message}
                </span>
              </div>
              <div className="w-full bg-primary-200/30 rounded-full h-3 overflow-hidden shadow-inner">
                <div
                  className="bg-gradient-primary h-3 rounded-full transition-all duration-500 shadow-lg progress-bar"
                  style={{ width: `${progressData.progress || 0}%` }}
                ></div>
              </div>
              <div className="text-xs font-body text-primary-600 dark:text-primary-400 mt-2">
                <span className="font-display font-semibold">{progressData.stage}</span> - {progressData.progress || 0}%
                {progressData.currentModel && (
                  <span className="ml-2 px-2 py-1 bg-primary-100/50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded-lg font-display font-medium border border-primary-200/30">
                    {progressData.currentModel}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Model Selection */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 gap-2 sm:gap-0">
            <label className="label text-xs sm:text-sm">
              Selected Models ({selectedModels.length})
              {availableModels.length > 0 && (
                <span className="font-normal text-primary-600 dark:text-primary-400 ml-1">
                  • {availableModels.length} available
                </span>
              )}
            </label>
            <button
              onClick={addModel}
              disabled={
                modelsLoading ||
                selectedModels.length >= availableModels.length ||
                availableModels.length === 0
              }
              className="btn btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Model</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
          <div className="overflow-y-auto space-y-3 sm:space-y-4 max-h-60 sm:max-h-80">
            {selectedModels.map((model, index) => (
              <div
                key={index}
                className="glass p-3 sm:p-4 shadow-lg backdrop-blur-xl border border-primary-200/30 sm:hover:scale-105 transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-3 sm:gap-0">
                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <select
                      value={`${model.provider}:${model.model}`}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        const [provider, ...modelParts] = val.split(":");
                        const selectedModel = modelParts.join(":");
                        const updated = [...selectedModels];
                        updated[index] = {
                          ...updated[index],
                          provider,
                          model: selectedModel,
                        };
                        setSelectedModels(updated);
                      }}
                      className="input text-xs sm:text-sm w-full"
                    >
                      {modelsLoading ? (
                        <option value="">Loading models...</option>
                      ) : (
                        <>
                          {allModels.length === 0 && (
                            <option value="">No models available</option>
                          )}
                          {(() => {
                            const available = allModels.filter(
                              (m) => !m.status || m.status === "available",
                            );
                            const unavailable = allModels.filter(
                              (m) => m.status === "unavailable",
                            );
                            const currentKey = `${model.provider}:${model.model}`;
                            const currentInList = allModels.some(
                              (m) => `${m.provider}:${m.model}` === currentKey,
                            );
                            return (
                              <>
                                {!currentInList && (
                                  <option value={currentKey}>
                                    {model.provider} - {model.model}
                                  </option>
                                )}
                                {available.map((m) => (
                                  <option
                                    key={`${m.provider}:${m.model}`}
                                    value={`${m.provider}:${m.model}`}
                                  >
                                    {m.provider} - {m.modelName || m.model}
                                  </option>
                                ))}
                                {unavailable.length > 0 && (
                                  <>
                                    <option disabled>── Unavailable ──</option>
                                    {unavailable.map((m) => (
                                      <option
                                        key={`${m.provider}:${m.model}`}
                                        value={`${m.provider}:${m.model}`}
                                        disabled
                                      >
                                        {m.provider} - {m.modelName || m.model}{" "}
                                        (unavailable)
                                      </option>
                                    ))}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </select>
                    {/* Show duplicate warning */}
                    {selectedModels.filter(
                      (m) =>
                        m.provider === model.provider &&
                        m.model === model.model,
                    ).length > 1 && (
                        <div className="glass p-2 rounded-lg border border-accent-200/30 bg-accent-500/10 dark:bg-accent-500/20 mt-2">
                          <div className="text-xs font-body text-accent-700 dark:text-accent-300 flex items-center">
                            <AlertTriangle className="h-3 w-3 mr-2" />
                            Duplicate model selected
                          </div>
                        </div>
                      )}
                    {(() => {
                      const pricing = getModelPricing(
                        model.provider,
                        model.model,
                      );
                      if (!pricing) return null;
                      const estCost1K =
                        (pricing.input + pricing.output) / 2000;
                      const modelMeta = availableModels.find(
                        (m) => m.provider === model.provider && m.model === model.model,
                      ) ?? AWS_BEDROCK_PRICING.find(
                        (m) =>
                          m.model === model.model ||
                          model.model.includes(m.model),
                      );
                      return (
                        <div className="glass p-2 sm:p-3 rounded-lg border border-primary-200/30 mt-2 space-y-1">
                          <div className="text-xs font-body text-light-text-secondary dark:text-dark-text-secondary">
                            <span className="font-display font-semibold text-success-600">Input:</span> ${pricing.input.toFixed(2)}/1M •
                            <span className="font-display font-semibold text-primary-600">Output:</span> ${pricing.output.toFixed(2)}/1M
                          </div>
                          <div className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">
                            <span className="font-display font-semibold">Est. Cost/1K:</span> ${estCost1K.toFixed(6)}
                            {modelMeta?.contextWindow && (
                              <>
                                {" • "}
                                <span className="font-display font-semibold">Context:</span> {modelMeta.contextWindow.toLocaleString()} tokens
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => removeModel(index)}
                    className="btn btn-ghost sm:ml-3 p-2 text-danger-500 hover:text-white hover:bg-gradient-danger self-start sm:self-auto"
                    title="Remove this model"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div>
                    <label className="block mb-1 sm:mb-2 text-xs font-display font-semibold text-light-text-primary dark:text-dark-text-primary">
                      Temperature
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={model.temperature}
                      onChange={(e) =>
                        updateModel(
                          index,
                          "temperature",
                          parseFloat(e.target.value),
                        )
                      }
                      className="input text-xs sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 sm:mb-2 text-xs font-display font-semibold text-light-text-primary dark:text-dark-text-primary">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="4000"
                      value={model.maxTokens}
                      onChange={(e) =>
                        updateModel(
                          index,
                          "maxTokens",
                          parseInt(e.target.value),
                        )
                      }
                      className="input text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
            {selectedModels.length === 0 && (
              <div className="text-center py-8">
                <div className="bg-gradient-primary p-4 rounded-2xl shadow-2xl glow-primary w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Beaker className="h-8 w-8 text-white" />
                </div>
                {modelsLoading ? (
                  <div className="text-sm font-body text-light-text-secondary dark:text-dark-text-secondary">
                    Loading models...
                  </div>
                ) : (
                  <div className="text-sm font-body text-light-text-secondary dark:text-dark-text-secondary">
                    No models selected. Click{" "}
                    <span className="font-display font-semibold text-primary-500">"Add Model"</span>{" "}
                    to get started.
                    {availableModels.length > 0 && (
                      <span className="block mt-1 text-xs text-primary-600 dark:text-primary-400">
                        {availableModels.length} model{availableModels.length !== 1 ? "s" : ""} available
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Evaluation Criteria */}
        <div>
          <label className="label mb-3 sm:mb-4 text-xs sm:text-sm">
            Evaluation Criteria
          </label>
          <div className="overflow-y-auto space-y-2 sm:space-y-3 max-h-60 sm:max-h-80">
            {criteriaOptions.map((criterion) => (
              <label key={criterion} className="flex items-center glass p-2 sm:p-3 rounded-xl border border-primary-200/30 hover:bg-primary-500/5 transition-all duration-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={evaluationCriteria.includes(criterion)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEvaluationCriteria([...evaluationCriteria, criterion]);
                    } else {
                      setEvaluationCriteria(
                        evaluationCriteria.filter((c) => c !== criterion),
                      );
                    }
                  }}
                  className="mr-2 sm:mr-3 w-4 h-4 text-primary-600 rounded border-primary-300 focus:ring-primary-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm font-display font-medium text-light-text-primary dark:text-dark-text-primary capitalize">
                  {criterion.replace("_", " ")}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional Settings */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 lg:col-span-2">
          <div>
            <label className="label mb-2 text-xs sm:text-sm">
              Iterations
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={iterations}
              onChange={(e) => setIterations(parseInt(e.target.value))}
              className="input w-full sm:w-24 text-xs sm:text-sm"
            />
          </div>
          {estimatedCost !== null && (
            <div className="glass p-3 sm:p-4 rounded-xl border border-success-200/30 bg-success-500/5 dark:bg-success-500/20 w-full sm:w-auto">
              <div className="flex items-center text-xs sm:text-sm">
                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-success-500 dark:text-success-400 flex-shrink-0" />
                <span className="font-body text-light-text-secondary dark:text-dark-text-secondary mr-2">Estimated Cost:</span>
                <span className="font-display font-bold gradient-text text-base sm:text-lg">
                  ${estimatedCost < 0.001
                    ? estimatedCost.toFixed(6)
                    : estimatedCost.toFixed(4)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fine-tuning analysis (backend heuristic) */}
      <div className="mb-6 rounded-xl border border-primary-200/30 bg-light-bg-secondary/30 dark:bg-dark-bg-secondary/30 p-4">
        <button
          type="button"
          onClick={() => setFineTuningOpen(!fineTuningOpen)}
          className="flex w-full items-center justify-between text-left font-display font-semibold text-light-text-primary dark:text-dark-text-primary"
        >
          Fine-tuning ROI analysis
          <span className="text-xs text-light-text-muted">
            {fineTuningOpen ? "Hide" : "Show"}
          </span>
        </button>
        {fineTuningOpen && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              Heuristic analysis from your usage patterns (not a training job).
            </p>
            <button
              type="button"
              className="btn btn-secondary text-sm"
              disabled={fineTuningLoading}
              onClick={async () => {
                setFineTuningLoading(true);
                setFineTuningData(null);
                try {
                  const data =
                    await ExperimentationService.getFineTuningAnalysis();
                  setFineTuningData(data);
                } catch (e) {
                  setError(
                    e instanceof Error
                      ? e.message
                      : "Fine-tuning analysis failed",
                  );
                } finally {
                  setFineTuningLoading(false);
                }
              }}
            >
              {fineTuningLoading ? "Loading…" : "Run analysis"}
            </button>
            {fineTuningData != null && (
              <pre className="text-xs overflow-auto max-h-64 rounded-lg border border-primary-200/30 p-3 bg-light-bg-100 dark:bg-dark-bg-100">
                {JSON.stringify(fineTuningData, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>

      {/* Recent comparison runs */}
      {comparisonHistory.length > 0 && (
        <div className="mb-6 rounded-xl border border-secondary-200/30 p-4">
          <h4 className="text-sm font-display font-semibold mb-2">
            Recent model comparisons
          </h4>
          {historyLoading ? (
            <p className="text-xs text-light-text-muted">Loading…</p>
          ) : (
            <ul className="space-y-1 text-xs max-h-32 overflow-y-auto">
              {comparisonHistory.map((h) => (
                <li key={h.id} className="truncate text-light-text-secondary">
                  {h.name} · {new Date(h.startTime).toLocaleString()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Results Section */}
      {(isRunning || results.length > 0) && (
        <div className="pt-4 sm:pt-6 md:pt-8 border-t border-primary-200/30">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
            <h3 className="text-xl sm:text-2xl font-display font-bold gradient-text">Results</h3>
            {currentExperiment && (
              <div className="glass p-2 sm:p-3 rounded-xl border border-primary-200/30 w-full sm:w-auto">
                <div className="text-xs sm:text-sm font-body text-light-text-secondary dark:text-dark-text-secondary">
                  <span className="font-display font-semibold">Experiment ID:</span> <span className="break-all">{currentExperiment.id}</span>
                </div>
              </div>
            )}
          </div>

          {isRunning && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="spinner-lg mb-4"></div>
                <span className="font-display font-semibold text-primary-600 dark:text-primary-400">Running comparison...</span>
              </div>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-4 sm:space-y-6">
              <ResultSummaryPanel
                results={results}
                comparisonAnalysis={comparisonAnalysis}
                experimentId={currentExperiment?.id ?? null}
              />
              {/* Winner banner - real-time comparison */}
              {comparisonAnalysis?.winner && (
                <div className="glass p-4 sm:p-5 shadow-lg backdrop-blur-xl border-2 border-primary-400/50 rounded-xl bg-gradient-to-r from-primary-500/10 to-success-500/10 animate-fade-in">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl" aria-hidden>
                      &#127942;
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-display font-bold text-primary-700 dark:text-primary-300">
                        Winner: {comparisonAnalysis.winner.model}
                      </h4>
                      <p className="text-sm font-body text-light-text-secondary dark:text-dark-text-secondary mt-1 leading-relaxed">
                        {comparisonAnalysis.winner.reason}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {results.map((result, index) => {
                const isRealTime =
                  result.costBreakdown != null ||
                  result.aiEvaluation != null;
                const overallScore =
                  result.aiEvaluation?.overall_score ??
                  result.aiEvaluation?.overallScore;
                const criteriaScores =
                  result.aiEvaluation?.criteria_scores ??
                  result.aiEvaluation?.criterion_scores ??
                  result.aiEvaluation?.criteriaScores ??
                  {};
                const actualCost =
                  result.actualCost ??
                  result.costBreakdown?.totalCost ??
                  0;
                const latency =
                  result.executionTime ?? result.metrics?.latency ?? 0;
                const inputTokens =
                  result.costBreakdown?.inputTokens ?? 0;
                const outputTokens =
                  result.costBreakdown?.outputTokens ?? 0;
                const recommendationText =
                  result.aiEvaluation?.recommendation ??
                  result.recommendation ??
                  "";

                return (
                  <div
                    key={result.id ?? index}
                    className="glass p-4 sm:p-5 md:p-6 shadow-lg backdrop-blur-xl border border-primary-200/30 sm:hover:scale-105 transition-all duration-300 animate-fade-in"
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-3 sm:gap-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h4 className="text-lg sm:text-xl font-display font-bold gradient-text break-words">
                            {result.provider} - {result.model}
                          </h4>
                          {isRealTime && overallScore != null && (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${overallScore >= 90
                                  ? "bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300"
                                  : overallScore >= 75
                                    ? "bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                                    : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                }`}
                            >
                              Score: {overallScore}/100
                            </span>
                          )}
                        </div>
                        {(recommendationText || !isRealTime) && (
                          <p className="text-xs sm:text-sm font-body text-light-text-secondary dark:text-dark-text-secondary mt-2 leading-relaxed">
                            {recommendationText || result.recommendation}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedResult(result);
                          setShowResultsModal(true);
                        }}
                        className="btn btn-secondary flex items-center gap-2 text-xs sm:text-sm w-full sm:w-auto justify-center"
                      >
                        <span className="hidden sm:inline">View Details</span>
                        <span className="sm:hidden">Details</span>
                      </button>
                    </div>

                    {result.actualUsage ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                        <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-primary/10 border-l-4 border-primary-500">
                          <div className="text-xs sm:text-sm font-display font-semibold text-primary-700 dark:text-primary-300">
                            Total Calls
                          </div>
                          <div className="text-lg sm:text-xl md:text-2xl font-display font-bold gradient-text">
                            {result.actualUsage.totalCalls.toLocaleString()}
                          </div>
                        </div>
                        <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-success/10 border-l-4 border-success-500">
                          <div className="text-xs sm:text-sm font-display font-semibold text-success-700 dark:text-success-300">
                            Avg Cost
                          </div>
                          <div className="text-lg sm:text-xl md:text-2xl font-display font-bold text-success-600 dark:text-success-400">
                            ${result.actualUsage.avgCost.toFixed(4)}
                          </div>
                        </div>
                        <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-accent/10 border-l-4 border-accent-500">
                          <div className="text-xs sm:text-sm font-display font-semibold text-accent-700 dark:text-accent-300">
                            Avg Response Time
                          </div>
                          <div className="text-lg sm:text-xl md:text-2xl font-display font-bold text-accent-600 dark:text-accent-400">
                            {result.actualUsage.avgResponseTime.toFixed(0)}ms
                          </div>
                        </div>
                        <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-danger/10 border-l-4 border-danger-500">
                          <div className="text-xs sm:text-sm font-display font-semibold text-danger-700 dark:text-danger-300">
                            Error Rate
                          </div>
                          <div className="text-lg sm:text-xl md:text-2xl font-display font-bold text-danger-600 dark:text-danger-400">
                            {result.actualUsage.errorRate.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    ) : isRealTime ? (
                      <div className="space-y-4">
                        {/* Row 2: 4-column metric grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                          <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-success/10 border-l-4 border-success-500">
                            <div className="text-xs sm:text-sm font-display font-semibold text-success-700 dark:text-success-300">
                              Actual Cost
                            </div>
                            <div className="text-lg sm:text-xl font-display font-bold text-success-600 dark:text-success-400">
                              ${actualCost.toFixed(4)}
                            </div>
                          </div>
                          <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-accent/10 border-l-4 border-accent-500">
                            <div className="text-xs sm:text-sm font-display font-semibold text-accent-700 dark:text-accent-300">
                              Latency
                            </div>
                            <div className="text-lg sm:text-xl font-display font-bold text-accent-600 dark:text-accent-400">
                              {latency.toLocaleString()}ms
                            </div>
                          </div>
                          <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-primary/10 border-l-4 border-primary-500">
                            <div className="text-xs sm:text-sm font-display font-semibold text-primary-700 dark:text-primary-300">
                              Input Tokens
                            </div>
                            <div className="text-lg sm:text-xl font-display font-bold gradient-text">
                              {inputTokens.toLocaleString()}
                            </div>
                          </div>
                          <div className="glass rounded-xl p-3 sm:p-4 bg-gradient-secondary/10 border-l-4 border-secondary-500">
                            <div className="text-xs sm:text-sm font-display font-semibold text-secondary-700 dark:text-secondary-300">
                              Output Tokens
                            </div>
                            <div className="text-lg sm:text-xl font-display font-bold text-secondary-600 dark:text-secondary-400">
                              {outputTokens.toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Row 3: Pricing rates ($/1M tokens) */}
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <div className="glass rounded-xl p-3 bg-gradient-primary/10 border-l-4 border-primary-500">
                            <div className="text-xs font-display font-semibold text-primary-700 dark:text-primary-300">
                              Input Rate
                            </div>
                            <div className="text-sm font-display font-bold gradient-text">
                              $
                              {result.costBreakdown?.inputTokens
                                ? (
                                  (result.costBreakdown.inputCost /
                                    result.costBreakdown.inputTokens) *
                                  1_000_000
                                ).toFixed(2)
                                : getModelPricing(
                                  result.provider,
                                  result.model,
                                )?.input?.toFixed(2) ?? "N/A"}
                              <span className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">
                                /1M tokens
                              </span>
                            </div>
                          </div>
                          <div className="glass rounded-xl p-3 bg-gradient-success/10 border-l-4 border-success-500">
                            <div className="text-xs font-display font-semibold text-success-700 dark:text-success-300">
                              Output Rate
                            </div>
                            <div className="text-sm font-display font-bold text-success-600 dark:text-success-400">
                              $
                              {result.costBreakdown?.outputTokens
                                ? (
                                  (result.costBreakdown.outputCost /
                                    result.costBreakdown.outputTokens) *
                                  1_000_000
                                ).toFixed(2)
                                : getModelPricing(
                                  result.provider,
                                  result.model,
                                )?.output?.toFixed(2) ?? "N/A"}
                              <span className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">
                                /1M tokens
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Row 4: Criteria score progress bars */}
                        {Object.keys(criteriaScores).length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-display font-semibold text-light-text-primary dark:text-dark-text-primary">
                              Evaluation Criteria
                            </div>
                            <div className="space-y-2">
                              {Object.entries(criteriaScores).map(
                                ([criterion, score]) => (
                                  <div
                                    key={criterion}
                                    className="flex items-center gap-2"
                                  >
                                    <span className="text-xs font-body capitalize w-24 sm:w-28 truncate">
                                      {criterion.replace(/_/g, " ")}
                                    </span>
                                    <div className="flex-1 h-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-primary-500 rounded-full transition-all duration-500"
                                        style={{
                                          width: `${Math.min(
                                            100,
                                            Math.max(0, score),
                                          )}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs font-display font-semibold w-10 text-right">
                                      {score}%
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                        {/* Row 5: Model response - collapsible */}
                        {(result.response ?? result.bedrockOutput) && (
                          <div className="mt-4 pt-4 border-t border-primary-200/30">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedResponseIndex(
                                  expandedResponseIndex === index ? null : index,
                                )
                              }
                              className="flex items-center gap-2 w-full text-left text-sm font-display font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                            >
                              <MessageSquare className="w-4 h-4" />
                              {expandedResponseIndex === index
                                ? "Hide model response"
                                : "View model response"}
                              {expandedResponseIndex === index ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedResponseIndex === index && (
                              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-primary-200/30 bg-light-bg-100 dark:bg-dark-bg-100 p-4">
                                <pre className="text-xs text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap font-sans">
                                  {result.response ?? result.bedrockOutput}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Pricing Information - legacy static comparison */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                          <div className="glass rounded-xl p-4 bg-gradient-primary/10 border-l-4 border-primary-500">
                            <div className="text-sm font-display font-semibold text-primary-700 dark:text-primary-300">
                              Input Cost
                            </div>
                            <div className="text-lg font-display font-bold gradient-text">
                              $
                              {(() => {
                                const pricing = result.pricing?.inputCost
                                  ? {
                                    input: result.pricing.inputCost * 1000000,
                                    output: result.pricing.outputCost || 0,
                                  }
                                  : getModelPricing(
                                    result.provider,
                                    result.model,
                                  );
                                return pricing?.input
                                  ? pricing.input.toFixed(2)
                                  : "N/A";
                              })()}
                              <span className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">/1M tokens</span>
                            </div>
                          </div>
                          <div className="glass rounded-xl p-4 bg-gradient-success/10 border-l-4 border-success-500">
                            <div className="text-sm font-display font-semibold text-success-700 dark:text-success-300">
                              Output Cost
                            </div>
                            <div className="text-lg font-display font-bold text-success-600 dark:text-success-400">
                              $
                              {(() => {
                                const pricing = result.pricing?.outputCost
                                  ? {
                                    input: result.pricing.inputCost || 0,
                                    output: result.pricing.outputCost * 1000000,
                                  }
                                  : getModelPricing(
                                    result.provider,
                                    result.model,
                                  );
                                return pricing?.output
                                  ? pricing.output.toFixed(2)
                                  : "N/A";
                              })()}
                              <span className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">/1M tokens</span>
                            </div>
                          </div>
                          <div className="glass rounded-xl p-4 bg-gradient-accent/10 border-l-4 border-accent-500">
                            <div className="text-sm font-display font-semibold text-accent-700 dark:text-accent-300">
                              Est. Cost/1K
                            </div>
                            <div className="text-lg font-display font-bold text-accent-600 dark:text-accent-400">
                              ${result.estimatedCostPer1K?.toFixed(4) || "N/A"}
                            </div>
                          </div>
                          <div className="glass rounded-xl p-4 bg-gradient-secondary/10 border-l-4 border-secondary-500">
                            <div className="text-sm font-display font-semibold text-secondary-700 dark:text-secondary-300">
                              Context Window
                            </div>
                            <div className="text-lg font-display font-bold text-secondary-600 dark:text-secondary-400">
                              {result.pricing?.contextWindow?.toLocaleString() ||
                                "N/A"}{" "}
                              <span className="text-xs font-body text-light-text-muted dark:text-dark-text-muted">tokens</span>
                            </div>
                          </div>
                        </div>

                        {/* Analysis Strengths and Considerations */}
                        {result.analysis &&
                          (result.analysis.strengths?.length > 0 ||
                            result.analysis.considerations?.length > 0) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {result.analysis.strengths?.length > 0 && (
                                <div className="glass p-4 bg-gradient-success/10 border-l-4 border-success-500">
                                  <div className="text-sm font-display font-semibold text-success-700 dark:text-success-300 mb-3">
                                    Strengths
                                  </div>
                                  <ul className="text-sm font-body text-success-600 dark:text-success-400 space-y-2">
                                    {result.analysis.strengths.map(
                                      (strength: string, i: number) => (
                                        <li key={i} className="flex items-start">
                                          <Check className="w-4 h-4 text-success-500 mr-2 flex-shrink-0 mt-0.5" />
                                          {strength}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                              {result.analysis.considerations?.length > 0 && (
                                <div className="glass p-4 bg-gradient-accent/10 border-l-4 border-accent-500">
                                  <div className="text-sm font-display font-semibold text-accent-700 dark:text-accent-300 mb-3">
                                    Considerations
                                  </div>
                                  <ul className="text-sm font-body text-accent-600 dark:text-accent-400 space-y-2">
                                    {result.analysis.considerations.map(
                                      (consideration: string, i: number) => (
                                        <li key={i} className="flex items-start">
                                          <AlertTriangle className="w-4 h-4 text-accent-500 dark:text-accent-400 mr-2 flex-shrink-0 mt-0.5" />
                                          <span>{consideration}</span>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}

                        {/* Model response - collapsible (legacy branch) */}
                        {(result.response ?? result.bedrockOutput) && (
                          <div className="mt-4 pt-4 border-t border-primary-200/30">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedResponseIndex(
                                  expandedResponseIndex === index ? null : index,
                                )
                              }
                              className="flex items-center gap-2 w-full text-left text-sm font-display font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                            >
                              <MessageSquare className="w-4 h-4" />
                              {expandedResponseIndex === index
                                ? "Hide model response"
                                : "View model response"}
                              {expandedResponseIndex === index ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedResponseIndex === index && (
                              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-primary-200/30 bg-light-bg-100 dark:bg-dark-bg-100 p-4">
                                <pre className="text-xs text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap font-sans">
                                  {result.response ?? result.bedrockOutput}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Comparison summary - real-time (comparisonAnalysis) or static (currentExperiment) */}
              {(comparisonAnalysis || (currentExperiment && currentExperiment.results)) && (
                <div className="mt-8 glass p-6 shadow-2xl backdrop-blur-xl border border-primary-200/30 animate-fade-in">
                  <h4 className="text-lg font-display font-bold gradient-text mb-4">
                    Overall Analysis
                  </h4>

                  {comparisonAnalysis ? (
                    <>
                      {results.length > 0 && (
                        <div className="glass p-3 rounded-xl border border-success-200/30 bg-success-500/5 mb-3">
                          <div className="text-sm font-body text-success-700 dark:text-success-300">
                            <span className="font-display font-semibold">Total session cost:</span>{" "}
                            $
                            {results
                              .reduce(
                                (sum, r) =>
                                  sum +
                                  (r.actualCost ?? r.costBreakdown?.totalCost ?? 0),
                                0,
                              )
                              .toFixed(4)}
                          </div>
                        </div>
                      )}

                      {comparisonAnalysis.costPerformanceAnalysis && (
                        <div className="glass p-3 rounded-xl border border-primary-200/30 bg-primary-500/5 mb-3">
                          <div className="text-sm font-body text-primary-700 dark:text-primary-300">
                            <span className="font-display font-semibold">Cost & performance:</span>{" "}
                            {comparisonAnalysis.costPerformanceAnalysis}
                          </div>
                        </div>
                      )}

                      {comparisonAnalysis.useCaseRecommendations &&
                        comparisonAnalysis.useCaseRecommendations.length > 0 && (
                          <div className="glass p-3 rounded-xl border border-accent-200/30 bg-accent-500/5 mb-3">
                            <div className="text-sm font-display font-semibold text-accent-700 dark:text-accent-300 mb-2">
                              Use case recommendations
                            </div>
                            <ul className="text-sm font-body text-accent-600 dark:text-accent-400 space-y-1 list-disc list-inside">
                              {comparisonAnalysis.useCaseRecommendations.map(
                                (rec, i) => (
                                  <li key={i}>{rec}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-body text-light-text-primary dark:text-dark-text-primary mb-4 leading-relaxed">
                        {currentExperiment.results.recommendation}
                      </p>

                      {currentExperiment.results.costComparison && (
                        <div className="glass p-3 rounded-xl border border-success-200/30 bg-success-500/5 mb-3">
                          <div className="text-sm font-body text-success-700 dark:text-success-300">
                            <span className="font-display font-semibold">Cost Comparison:</span>{" "}
                            {currentExperiment.results.costComparison}
                          </div>
                        </div>
                      )}

                      {currentExperiment.results.useCaseAnalysis && (
                        <div className="glass p-3 rounded-xl border border-primary-200/30 bg-primary-500/5 mb-3">
                          <div className="text-sm font-body text-primary-700 dark:text-primary-300">
                            <span className="font-display font-semibold">Use Case Analysis:</span>{" "}
                            {currentExperiment.results.useCaseAnalysis}
                          </div>
                        </div>
                      )}

                      <div className="glass p-3 rounded-xl border border-accent-200/30 bg-accent-500/5">
                        <div className="text-xs font-body text-accent-700 dark:text-accent-300">
                          <span className="font-display font-semibold">Confidence:</span>{" "}
                          <span className="gradient-text font-display font-bold">
                            {(
                              (currentExperiment.metadata.confidence || 0) * 100
                            ).toFixed(1)}%
                          </span>
                          {" "} • Based on{" "}
                          <span className="font-display font-semibold">
                            {currentExperiment.results.basedOnActualUsage || 0}
                          </span>{" "}
                          models with usage data
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results Detail Modal */}
      {showResultsModal && selectedResult && (
        <Modal
          isOpen={showResultsModal}
          onClose={() => setShowResultsModal(false)}
          title={`${selectedResult.provider} - ${selectedResult.model}`}
        >
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Recommendation
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  {selectedResult.aiEvaluation?.recommendation ??
                    selectedResult.recommendation ??
                    "—"}
                </p>
              </div>
            </div>

            {(selectedResult.costBreakdown != null ||
              selectedResult.aiEvaluation != null) ? (
              <div className="space-y-4">
                {/* Token cost breakdown table */}
                {selectedResult.costBreakdown && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Token Cost Breakdown
                    </h4>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-gray-100">
                              Metric
                            </th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                              Value
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          <tr>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                              Input Tokens
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {selectedResult.costBreakdown.inputTokens.toLocaleString()}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                              Output Tokens
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {selectedResult.costBreakdown.outputTokens.toLocaleString()}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                              Input Cost
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              $
                              {selectedResult.costBreakdown.inputCost.toFixed(6)}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                              Output Cost
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              $
                              {selectedResult.costBreakdown.outputCost.toFixed(6)}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-semibold">
                              Total Cost
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-semibold">
                              $
                              {selectedResult.costBreakdown.totalCost.toFixed(4)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Per-criterion reasoning */}
                {selectedResult.aiEvaluation?.reasoning &&
                  typeof selectedResult.aiEvaluation.reasoning === "object" && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Evaluation Reasoning
                      </h4>
                      <div className="space-y-3">
                        {Object.entries(
                          selectedResult.aiEvaluation.reasoning as Record<
                            string,
                            unknown
                          >,
                        ).map(([criterion, value]) => {
                          if (typeof value === "string") {
                            return (
                              <div
                                key={criterion}
                                className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3"
                              >
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize mb-1">
                                  {criterion.replace(/_/g, " ")}
                                </div>
                                <p className="text-sm text-gray-800 dark:text-gray-200">
                                  {value}
                                </p>
                              </div>
                            );
                          }
                          if (
                            value &&
                            typeof value === "object" &&
                            !Array.isArray(value)
                          ) {
                            const v = value as {
                              strengths?: string[];
                              weaknesses?: string[];
                            };
                            return (
                              <div
                                key={criterion}
                                className="space-y-2"
                              >
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize">
                                  {criterion.replace(/_/g, " ")}
                                </div>
                                {v.strengths?.length ? (
                                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                                    <div className="text-xs font-semibold text-green-800 dark:text-green-300 mb-1">
                                      Strengths
                                    </div>
                                    <ul className="text-sm text-green-700 dark:text-green-200 space-y-1 list-disc list-inside">
                                      {v.strengths.map((s, i) => (
                                        <li key={i}>{s}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                                {v.weaknesses?.length ? (
                                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
                                    <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
                                      Weaknesses
                                    </div>
                                    <ul className="text-sm text-amber-700 dark:text-amber-200 space-y-1 list-disc list-inside">
                                      {v.weaknesses.map((w, i) => (
                                        <li key={i}>{w}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}

                {/* Fallback: reasoning as string */}
                {selectedResult.aiEvaluation?.reasoning &&
                  typeof selectedResult.aiEvaluation.reasoning === "string" && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Evaluation Reasoning
                      </h4>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                          {selectedResult.aiEvaluation.reasoning}
                        </p>
                      </div>
                    </div>
                  )}

                {/* Strengths / Weaknesses (top-level) */}
                {(selectedResult.aiEvaluation?.strengths?.length ??
                  0) > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Strengths
                      </h4>
                      <ul className="text-sm text-gray-800 dark:text-gray-200 space-y-1 list-disc list-inside">
                        {selectedResult.aiEvaluation?.strengths?.map(
                          (s, i) => (
                            <li key={i}>{s}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                {(selectedResult.aiEvaluation?.weaknesses?.length ?? 0) > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Weaknesses
                    </h4>
                    <ul className="text-sm text-gray-800 dark:text-gray-200 space-y-1 list-disc list-inside">
                      {selectedResult.aiEvaluation?.weaknesses?.map(
                        (w, i) => (
                          <li key={i}>{w}</li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

                {/* Model response */}
                {(selectedResult.response ?? selectedResult.bedrockOutput) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Model Response
                    </h4>
                    <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
                      <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans">
                        {selectedResult.response ??
                          selectedResult.bedrockOutput}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ) : selectedResult.actualUsage ? (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Usage Statistics
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-900 mb-2">
                      Call Statistics
                    </div>
                    <div className="space-y-1 text-sm text-blue-800">
                      <div>
                        Total Calls:{" "}
                        {selectedResult.actualUsage.totalCalls.toLocaleString()}
                      </div>
                      <div>
                        Total Cost: $
                        {selectedResult.actualUsage.totalCost.toFixed(2)}
                      </div>
                      <div>
                        Avg Cost: $
                        {selectedResult.actualUsage.avgCost.toFixed(4)}
                      </div>
                      <div>
                        Avg Tokens:{" "}
                        {selectedResult.actualUsage.avgTokens.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-green-900 mb-2">
                      Performance
                    </div>
                    <div className="space-y-1 text-sm text-green-800">
                      <div>
                        Avg Response Time:{" "}
                        {selectedResult.actualUsage.avgResponseTime.toFixed(0)}
                        ms
                      </div>
                      <div>
                        Error Rate:{" "}
                        {selectedResult.actualUsage.errorRate.toFixed(2)}%
                      </div>
                      <div>
                        Success Rate:{" "}
                        {(100 - selectedResult.actualUsage.errorRate).toFixed(
                          2,
                        )}
                        %
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Pricing Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-blue-900 mb-2">
                        Token Costs
                      </div>
                      <div className="space-y-1 text-sm text-blue-800">
                        <div>
                          Input: $
                          {(() => {
                            const pricing = selectedResult.pricing?.inputCost
                              ? {
                                input:
                                  selectedResult.pricing.inputCost * 1000000,
                                output:
                                  selectedResult.pricing.outputCost || 0,
                              }
                              : getModelPricing(
                                selectedResult.provider,
                                selectedResult.model,
                              );
                            return pricing?.input
                              ? pricing.input.toFixed(2)
                              : "N/A";
                          })()}
                          /1M tokens
                        </div>
                        <div>
                          Output: $
                          {(() => {
                            const pricing = selectedResult.pricing?.outputCost
                              ? {
                                input: selectedResult.pricing.inputCost || 0,
                                output:
                                  selectedResult.pricing.outputCost * 1000000,
                              }
                              : getModelPricing(
                                selectedResult.provider,
                                selectedResult.model,
                              );
                            return pricing?.output
                              ? pricing.output.toFixed(2)
                              : "N/A";
                          })()}
                          /1M tokens
                        </div>
                        <div className="font-medium">
                          Est. Cost per 1K: $
                          {(() => {
                            if (selectedResult.estimatedCostPer1K)
                              return selectedResult.estimatedCostPer1K.toFixed(
                                4,
                              );
                            const pricing = getModelPricing(
                              selectedResult.provider,
                              selectedResult.model,
                            );
                            if (pricing) {
                              // Calculate estimated cost per 1K tokens (assume 50% input, 50% output)
                              const cost1K =
                                (pricing.input * 0.5 + pricing.output * 0.5) /
                                1000;
                              return cost1K.toFixed(4);
                            }
                            return "N/A";
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-purple-900 mb-2">
                        Model Specs
                      </div>
                      <div className="space-y-1 text-sm text-purple-800">
                        <div>
                          Context Window:{" "}
                          {selectedResult.pricing?.contextWindow?.toLocaleString() ||
                            "N/A"}{" "}
                          tokens
                        </div>
                        <div>Provider: {selectedResult.provider}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedResult.analysis && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedResult.analysis.strengths?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Strengths
                        </h4>
                        <div className="bg-green-50 rounded-lg p-4">
                          <ul className="text-sm text-green-800 space-y-2">
                            {selectedResult.analysis.strengths.map(
                              (strength: string, i: number) => (
                                <li key={i} className="flex items-start">
                                  <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                                  <span>{strength}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      </div>
                    )}

                    {selectedResult.analysis.considerations?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Considerations
                        </h4>
                        <div className="bg-amber-50 rounded-lg p-4">
                          <ul className="text-sm text-amber-800 space-y-2">
                            {selectedResult.analysis.considerations.map(
                              (consideration: string, i: number) => (
                                <li key={i} className="flex items-start">
                                  <AlertTriangle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                                  <span>{consideration}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedResult.estimatedCostPer1K === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>No pricing data available</strong> - This model
                      may not be included in our pricing database. Please check
                      with the provider for current pricing information.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ModelComparison;
